import type {
  ApplyFiltersResponse,
  ApplyMappingsResponse,
  BulkUpdateItem,
  DataImport,
  FilterSelection,
  ImportRemainingAs,
  WizardStep,
} from "@/schema/data-import"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  applyFilters,
  applyMappings,
  bulkUpdateReviewItems,
  startImport,
  submitTwoFactorCode,
  updateDataImport,
  uploadFileToImport,
} from "./api"
import { dataImportsQueryKeys } from "./queries"

export function useApplyFilters(dataImportId: string) {
  const getContext = useApiContext()
  const qc = useQueryClient()

  return useMutation<
    ApplyFiltersResponse,
    Error,
    { import_mode: "all" | "filtered"; import_filters?: FilterSelection[] }
  >({
    mutationFn: async (params) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return applyFilters(ctx, dataImportId, params)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["data-import", dataImportId] })
    },
  })
}

export function useApplyMappings(dataImportId: string) {
  const getContext = useApiContext()
  const qc = useQueryClient()

  return useMutation<
    ApplyMappingsResponse,
    Error,
    {
      client_filters?: FilterSelection[]
      past_client_filters?: FilterSelection[]
      prospect_filters?: FilterSelection[]
      import_remaining_items_as?: ImportRemainingAs
    }
  >({
    mutationFn: async (params) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return applyMappings(ctx, dataImportId, params)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["data-import", dataImportId] })
    },
  })
}

export function useUpdateDataImport(dataImportId: string) {
  const getContext = useApiContext()
  const qc = useQueryClient()

  return useMutation<DataImport, Error, { wizard_step: WizardStep }>({
    mutationFn: async (params) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return updateDataImport(ctx, dataImportId, params)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["data-import", dataImportId] })
      qc.invalidateQueries({ queryKey: dataImportsQueryKeys.all })
    },
  })
}

export function useBulkUpdateReviewItems(dataImportId: string) {
  const getContext = useApiContext()
  const qc = useQueryClient()

  return useMutation<void, Error, BulkUpdateItem[]>({
    mutationFn: async (updates) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return bulkUpdateReviewItems(ctx, dataImportId, updates)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["data-import", dataImportId, "review-items"] })
    },
  })
}

export function useStartImport(dataImportId: string) {
  const getContext = useApiContext()
  const qc = useQueryClient()

  return useMutation<DataImport, Error, void>({
    mutationFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return startImport(ctx, dataImportId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["data-import", dataImportId] })
      qc.invalidateQueries({ queryKey: ["data-imports"] })
    },
  })
}

export function useSubmitTwoFactorCode(dataImportId: string) {
  const getContext = useApiContext()
  const qc = useQueryClient()

  return useMutation<DataImport, Error, string>({
    mutationFn: async (code) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return submitTwoFactorCode(ctx, dataImportId, code)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["data-import", dataImportId] })
      qc.invalidateQueries({ queryKey: ["data-imports"] })
    },
  })
}

export function useUploadFileToImport(dataImportId: string) {
  const getContext = useApiContext()
  const qc = useQueryClient()

  return useMutation<DataImport, Error, File>({
    mutationFn: async (file) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return uploadFileToImport(ctx, dataImportId, file, getContext)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["data-import", dataImportId] })
    },
  })
}
